import React, {
  useMemo,
  useCallback,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@components/Layout';
import ImageUploader from '@components/ImageUploader';
import ReactQuill from 'react-quill';
import { toast } from 'react-hot-toast';
import { db } from '@root/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { createRandomString } from '@utils/FilesFolders';

// Address
import useAddressState from '@hooks/useAddressState';
import AddressEditor from '@components/AddressEditor';

function CreateEvent({ menuValue }) {
  const { globalUser, globalData } = useAuth();
  const uploaderRef = useRef(null);

  // form state
  const [eventTime, setEventTime] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [mainTitle, setMainTitle] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [message, setMessage] = useState('');
  const [imageUrl, setImageUrl] = useState(''); // from ImageUploader
  const [isSaving, setIsSaving] = useState(false);

  // address
  const {
    addrQuery,
    setAddrQuery,
    addressFieldsState,
    handleAddressChange,
    applyTomTomPlace,
  } = useAddressState(globalData);

  // ---------- helpers ----------
  const trim = (v) => String(v ?? '').trim();

  const requiredAddressKeys = useMemo(
    () => ['street', 'city', 'region', 'zip', 'country'],
    []
  );

  // inside CreateEvent component

  // reset helper (already defined in the last version)
  const resetAddress = useCallback(() => {
    setAddrQuery('');
    ['street', 'city', 'region', 'country', 'zip'].forEach((f) =>
      handleAddressChange(f, '')
    );
  }, [handleAddressChange, setAddrQuery]);

  // when component first loads (or every time menuValue changes) reset address
  useEffect(() => {
    resetAddress();
  }, [menuValue, resetAddress]);

  const validate = useCallback(() => {
    // image
    if (!trim(imageUrl)) {
      toast.error('Please select an event image.');
      return false;
    }
    if (!trim(mainTitle)) {
      toast.error('Main title cannot be empty.');
      return false;
    }
    if (!trim(subTitle)) {
      toast.error('Sub title cannot be empty.');
      return false;
    }
    if (!trim(eventDate)) {
      toast.error('Please select event date.');
      return false;
    }
    if (!trim(eventTime)) {
      toast.error('Please select event time.');
      return false;
    }
    // address
    for (const key of requiredAddressKeys) {
      if (!trim(addressFieldsState?.[key])) {
        toast.error(
          `${key.charAt(0).toUpperCase() + key.slice(1)} is required`
        );
        return false;
      }
    }
    if (!trim(message)) {
      toast.error('Please enter a message.');
      return false;
    }
    return true;
  }, [
    imageUrl,
    mainTitle,
    subTitle,
    eventDate,
    eventTime,
    message,
    addressFieldsState,
    requiredAddressKeys,
  ]);

  // Preview (derived from form state)
  const preview = useMemo(
    () => ({
      image: imageUrl,
      mainTitle: trim(mainTitle),
      subTitle: trim(subTitle),
      message, // keep HTML
      eventDate: trim(eventDate),
      eventTime: trim(eventTime),
    }),
    [imageUrl, mainTitle, subTitle, message, eventDate, eventTime]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validate()) return;

      try {
        setIsSaving(true);

        const uniqueKey = createRandomString(30);
        const memorialDocRef = doc(db, `memorials/${uniqueKey}`);

        const payload = {
          file: `${uniqueKey}.jpg`,
          image: imageUrl,
          tribute: menuValue,
          mainTitle: trim(mainTitle),
          subTitle: trim(subTitle),
          eventDate: preview.eventDate,
          eventTime: preview.eventTime,
          message, // HTML
          uid: globalUser.uid,
          createdDate: new Date(),
          address: addressFieldsState,
          tributeId: uniqueKey,
          privacy: 'Public',
        };

        await setDoc(memorialDocRef, payload, { merge: true });
        toast.success('Tribute saved.');

        // optional: clear form
        // setMainTitle(''); setSubTitle(''); setEventDate(''); setEventTime(''); setMessage(''); setImageUrl(''); resetAddress();
      } catch (err) {
        console.error('Error Saving Tribute', err);
        toast.error('Failed saving tribute.');
      } finally {
        setIsSaving(false);
      }
    },
    [
      validate,
      imageUrl,
      menuValue,
      mainTitle,
      subTitle,
      preview.eventDate,
      preview.eventTime,
      message,
      globalUser?.uid,
      addressFieldsState,
      resetAddress,
    ]
  );

  return (
    <>
      {/* Middle */}
      <div className='col-lg-5'>
        <div className='card'>
          <h5 className='card-header bg-transparent border-bottom text-uppercase'>
            Create a {menuValue}
          </h5>
          <div className='card-body'>
            <form onSubmit={handleSubmit}>
              <div className='row'>
                {/* Image */}
                <div className='col-12'>
                  <ImageUploader
                    ref={uploaderRef}
                    uid={globalData?.uid}
                    onUpload={(url) => setImageUrl(url)}
                    // if you want to preview as soon as a file is selected (before upload), add onFileSelect here
                  />
                </div>

                <div className='col-6 form-floating mt-3'>
                  <input
                    className='form-control'
                    type='text'
                    value={mainTitle}
                    onChange={(e) => setMainTitle(e.target.value)}
                    placeholder='Event Title'
                  />
                  <label>Event Title</label>
                </div>

                <div className='col-6 form-floating mt-3'>
                  <input
                    className='form-control'
                    type='text'
                    value={subTitle}
                    onChange={(e) => setSubTitle(e.target.value)}
                    placeholder='Sub Title'
                  />
                  <label>Sub Title</label>
                </div>

                <div className='col-6 form-floating mt-3'>
                  <input
                    className='form-control'
                    type='date'
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                  />
                  <label>Event Date</label>
                </div>

                <div className='col-6 form-floating mt-3'>
                  <input
                    className='form-control'
                    type='time'
                    value={eventTime}
                    onChange={(e) => setEventTime(e.target.value)}
                  />
                  <label>Event Time</label>
                </div>

                {/* Address */}
                <div className='col-12 mt-3'>
                  <label htmlFor='autocomplete' className='form-label'>
                    Search for Address
                  </label>
                  <div className='row g-3'>
                    <AddressEditor
                      addrQuery={addrQuery}
                      setAddrQuery={setAddrQuery}
                      addressFieldsState={addressFieldsState}
                      handleAddressChange={handleAddressChange}
                      applyTomTomPlace={applyTomTomPlace}
                    />
                  </div>
                </div>

                {/* Message */}
                <div className='col-12 mt-3'>
                  <ReactQuill value={message} onChange={setMessage} />
                </div>

                {/* Submit */}
                <div className='col-12 mt-3'>
                  <button
                    type='submit'
                    disabled={isSaving}
                    className={`btn btn-${isSaving ? 'dark' : 'primary'} waves-effect waves-light`}
                  >
                    {isSaving ? (
                      <>
                        <i className='bx bx-loader bx-spin font-size-16 align-middle me-2' />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className='bx bx-file me-1' /> Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right / Preview */}
      <div className='col-lg-4'>
        <div className='col-lg-12'>
          <div className='card'>
            <div className='row g-0 align-items-center'>
              <div className='col-md-12'>
                <div className='card-header bg-transparent border-bottom text-uppercase'>
                  <h5 className='card-title'>Preview</h5>
                  <p className='card-text'>View your tribute below..</p>
                </div>
                <div className='card-body'>
                  <div className='col-12'>
                    {preview.image ? (
                      <img
                        src={preview.image}
                        alt='Preview'
                        className='img-fluid rounded'
                        style={{
                          maxHeight: 300,
                          maxWidth: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <p>No image selected.</p>
                    )}
                  </div>

                  {preview.mainTitle && (
                    <div className='col-12 mt-3'>
                      <h3>{preview.mainTitle}</h3>
                    </div>
                  )}
                  {preview.subTitle && (
                    <div className='col-12'>
                      <h6>{preview.subTitle}</h6>
                    </div>
                  )}
                  {(preview.eventDate || preview.eventTime) && (
                    <div className='col-12'>
                      <h6>
                        {preview.eventDate || ''}{' '}
                        {preview.eventTime ? `• ${preview.eventTime}` : ''}
                      </h6>
                    </div>
                  )}
                  {preview.message && (
                    <div className='col-12'>
                      <div
                        dangerouslySetInnerHTML={{ __html: preview.message }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CreateEvent;
